import React from 'react';
import { useGroupContext } from '../lib/groups/context';

// Lightweight brand that derives the vault label from URL slug and an optional logo URL from localStorage
// Fallback: if no slug, shows "Create Vault" label; Clicking navigates to root or /:slug
// Local storage key for logo: gv:logoUrl:<slug>

function getSlugFromPath(): string {
    const firstSeg = (window.location.pathname.split('/').filter(Boolean)[0] || '').trim();
    const reserved = new Set(['auth', 'login', 'reset-password', 'code', 'preview']);
    return firstSeg && !reserved.has(firstSeg.toLowerCase()) ? firstSeg : '';
}

function prettifySlug(slug: string): string {
    if (!slug) return 'Create Vault';
    return slug
        .replace(/^\/+|\/+$/g, '')
        .split('-')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

export default function VaultBrand({ className = '', onNavigate }: { className?: string; onNavigate?: () => void }) {
    const [slug, setSlug] = React.useState<string>(() => getSlugFromPath());
    // Remove logo usage entirely per request; keep only name
    const [displayName, setDisplayName] = React.useState<string | null>(null);
    const { activeGroupId, groups, reload } = useGroupContext();

    const name = React.useMemo(() => {
        // If we have an active group, use its name
        if (activeGroupId && groups.length > 0) {
            const activeGroup = groups.find(g => g.group.id === activeGroupId);
            if (activeGroup) {
                return activeGroup.group.name;
            }
        }
        return displayName || prettifySlug(slug);
    }, [displayName, slug, activeGroupId, groups]);

    React.useEffect(() => {
        const onPop = () => setSlug(getSlugFromPath());
        window.addEventListener('popstate', onPop);
        window.addEventListener('pushstate', onPop as any);
        return () => {
            window.removeEventListener('popstate', onPop);
            window.removeEventListener('pushstate', onPop as any);
        };
    }, []);

    React.useEffect(() => {
        try {
            const nm = localStorage.getItem(`gv:vaultName:${slug}`);
            setDisplayName(nm || null);
        } catch {
            setDisplayName(null);
        }
    }, [slug]);

    const handleClick = () => {
        // Prefer a caller-provided navigate handler (lets Sidebar close on mobile)
        if (typeof onNavigate === 'function') {
            try { onNavigate(); } catch { /* ignore */ }
            return;
        }
        try {
            // When inside a vault (slug present), navigate to dashboard in-app
            if (slug) {
                window.dispatchEvent(new CustomEvent('gv:navigate' as any, { detail: { view: 'dashboard' } } as any));
                return;
            }
            // If no slug (root), go to root path
            window.history.pushState({}, '', '/');
            reload();
        } catch {
            window.location.href = slug ? `/${slug}` : '/';
        }
    };

    return (
        <button
            type="button"
            aria-label={name}
            className={`flex items-center gap-2 select-none focus:outline-none ${className}`}
            onClick={handleClick}
            title="Home"
        >
            <span className="text-sm font-semibold">{name}</span>
        </button>
    );
}
